import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http'; // New HTTP client (Angular 4.3+)
import { NavigationEnd, Router } from '@angular/router';

import { Company } from '../interfaces/company.interface';
import { Family } from '../interfaces/family.interface';
import { lastValueFrom } from 'rxjs';

import { environment } from "../../environments/environment"

@Injectable()
export class MechanicsService {

	public isLocalHost: boolean = window.location.hostname === "localhost";

	public patentsFilters: any = {};

	public appearanceConfig = {
		// "needLogin": can't search or access anything unless you are logged in
		// "normal": the site's normal, public appearance
		siteState: "normal", // ["pat-informed.ifpma.org"].includes(window.location.hostname) ? "needLogin": "normal",

		// real siteKey = 6LfJdisUAAAAAKCJM6utCE8xAsoAqkXk07dMYLhE
		// testing (Protractor) siteKey = 6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI
		captchaKey: ["localhost"].includes(window.location.hostname) ? "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI" : "6LfJdisUAAAAAKCJM6utCE8xAsoAqkXk07dMYLhE"
	}

	public isFrozen: boolean = false

	private _isLoading = signal(false)

	public get isLoading(): boolean {
		return this._isLoading();
	}

	public set isLoading(value: boolean) {
		const previousValue = this._isLoading();
		this._isLoading.set(value);

		console.log(`[MechanicsService.isLoading] ${previousValue} -> ${value}`, {
			stack: new Error().stack
		});
	}

	private _isAnalysingCSV = signal(false)

	public get isAnalysingCSV(): boolean {
		return this._isAnalysingCSV();
	}

	public set isAnalysingCSV(value: boolean) {
		this._isAnalysingCSV.set(value);
	}

	public modal = signal("")

	// public isListeningForMouseMove: boolean = false

	public display: any = {
		disconnectedMessage: false,
		innDetails: false,
		introText: true,
		loginMenu: false,

		modal: "",

		// modalOverlay: false,
		// modalAcceptance: null, // undefined state for now, neither true of false. Set to true or false in searchServide, after the first search
		// modalAccountCreated: false,
		// modalContactCompany: false,
		// modalInaccuracies: false,
		// modalLogin: false,
		// modalRecheckLinks: false,
		// modalVideo: false,
		// modalResolutionReport: false,

		refetchTitleButton: true,
		familyDrawer: false
	}

	public currentUrl: string = ""

	public initialSearchDone: boolean = false

	public companies: Company[] = [] // Fetched ASAP
	public vipEmails: string[] = []

	public condensedSearchContainer: boolean = false

	public selectedFamily: Family // When a search result is clicked and the family-details show up on the right

	public countries: Object = {
		WO: "WIPO",
		EP: "European Patent Office",
		OA: "African Intellectual Property Organization (OAPI)",
		AP: "African Regional Intellectual Property Organization (ARIPO)",
		EA: "Eurasian Patent Organization (EAPO)",
		GC: 'Gulf Cooperation Council',

		AD: 'Andorra',
		AE: 'United Arab Emirates',
		AF: 'Afghanistan',
		AG: 'Antigua and Barbuda',
		AI: 'Anguilla',
		AL: 'Albania',
		AM: 'Armenia',
		AN: 'Netherlands Antilles',
		AO: 'Angola',
		AQ: 'Antarctica',
		AR: 'Argentina',
		AS: 'American Samoa',
		AT: 'Austria',
		AU: 'Australia',
		AW: 'Aruba',
		AX: 'Åland Islands',
		AZ: 'Azerbaijan',
		BA: 'Bosnia and Herzegovina',
		BB: 'Barbados',
		BD: 'Bangladesh',
		BE: 'Belgium',
		BF: 'Burkina Faso',
		BG: 'Bulgaria',
		BH: 'Bahrain',
		BI: 'Burundi',
		BJ: 'Benin',
		BM: 'Bermuda',
		BN: 'Brunei Darussalam',
		BO: 'Bolivia',
		BR: 'Brazil',
		BS: 'Bahamas',
		BT: 'Bhutan',
		BV: 'Bouvet Island',
		BW: 'Botswana',
		BY: 'Belarus',
		BZ: 'Belize',
		CA: 'Canada',
		CC: 'Cocos (Keeling) Islands',
		CD: 'Congo, The Democratic Republic of the',
		CF: 'Central African Republic',
		CH: 'Switzerland',
		CI: 'Cote D\'Ivoire',
		CK: 'Cook Islands',
		CL: 'Chile',
		CM: 'Cameroon',
		CN: 'China',
		CO: 'Colombia',
		CR: 'Costa Rica',
		CS: 'Serbia and Montenegro (Old country code)',
		CU: 'Cuba',
		CV: 'Cape Verde',
		CX: 'Christmas Island',
		CY: 'Cyprus',
		CZ: 'Czech Republic',
		DE: 'Germany',
		DJ: 'Djibouti',
		DK: 'Denmark',
		DM: 'Dominica',
		DO: 'Dominican Republic',
		DZ: 'Algeria',
		EC: 'Ecuador',
		EE: 'Estonia',
		EG: 'Egypt',
		EH: 'Western Sahara',
		ER: 'Eritrea',
		ES: 'Spain',
		ET: 'Ethiopia',
		FI: 'Finland',
		FJ: 'Fiji',
		FK: 'Falkland Islands (Malvinas)',
		FM: 'Micronesia, Federated States of',
		FO: 'Faroe Islands',
		FR: 'France',
		GA: 'Gabon',
		GB: 'United Kingdom',
		GD: 'Grenada',
		GE: 'Georgia',
		GF: 'French Guiana',
		GG: 'Guernsey',
		GH: 'Ghana',
		GI: 'Gibraltar',
		GL: 'Greenland',
		GM: 'Gambia',
		GN: 'Guinea',
		GP: 'Guadeloupe',
		GQ: 'Equatorial Guinea',
		GR: 'Greece',
		GS: 'South Georgia and the South Sandwich Islands',
		GT: 'Guatemala',
		GU: 'Guam',
		GW: 'Guinea-Bissau',
		GY: 'Guyana',
		HK: 'Hong Kong',
		HM: 'Heard Island and Mcdonald Islands',
		HN: 'Honduras',
		HR: 'Croatia',
		HT: 'Haiti',
		HU: 'Hungary',
		ID: 'Indonesia',
		IE: 'Ireland',
		IL: 'Israel',
		IM: 'Isle of Man',
		IN: 'India',
		IO: 'British Indian Ocean Territory',
		IQ: 'Iraq',
		IR: 'Iran, Islamic Republic Of',
		IS: 'Iceland',
		IT: 'Italy',
		JE: 'Jersey',
		JM: 'Jamaica',
		JO: 'Jordan',
		JP: 'Japan',
		KE: 'Kenya',
		KG: 'Kyrgyzstan',
		KH: 'Cambodia',
		KI: 'Kiribati',
		KM: 'Comoros',
		KN: 'Saint Kitts and Nevis',
		KP: 'Korea, Democratic People\'S Republic of',
		KR: 'Korea, Republic of',
		KW: 'Kuwait',
		KY: 'Cayman Islands',
		KZ: 'Kazakhstan',
		LA: 'Lao People\'S Democratic Republic',
		LB: 'Lebanon',
		LC: 'Saint Lucia',
		LI: 'Liechtenstein',
		LK: 'Sri Lanka',
		LR: 'Liberia',
		LS: 'Lesotho',
		LT: 'Lithuania',
		LU: 'Luxembourg',
		LV: 'Latvia',
		LY: 'Libyan Arab Jamahiriya',
		MA: 'Morocco',
		MC: 'Monaco',
		MD: 'Moldova, Republic of',
		ME: 'Montenegro',
		MG: 'Madagascar',
		MH: 'Marshall Islands',
		MK: 'Macedonia, The Former Yugoslav Republic of',
		ML: 'Mali',
		MM: 'Myanmar',
		MN: 'Mongolia',
		MO: 'Macao',
		MP: 'Northern Mariana Islands',
		MQ: 'Martinique',
		MR: 'Mauritania',
		MS: 'Montserrat',
		MT: 'Malta',
		MU: 'Mauritius',
		MV: 'Maldives',
		MW: 'Malawi',
		MX: 'Mexico',
		MY: 'Malaysia',
		MZ: 'Mozambique',
		NA: 'Namibia',
		NC: 'New Caledonia',
		NE: 'Niger',
		NF: 'Norfolk Island',
		NG: 'Nigeria',
		NI: 'Nicaragua',
		NL: 'Netherlands',
		NO: 'Norway',
		NP: 'Nepal',
		NR: 'Nauru',
		NU: 'Niue',
		NZ: 'New Zealand',
		OM: 'Oman',
		PA: 'Panama',
		PE: 'Peru',
		PF: 'French Polynesia',
		PG: 'Papua New Guinea',
		PH: 'Philippines',
		PK: 'Pakistan',
		PL: 'Poland',
		PM: 'Saint Pierre and Miquelon',
		PN: 'Pitcairn',
		PR: 'Puerto Rico',
		PS: 'Palestinian Territory, Occupied',
		PT: 'Portugal',
		PW: 'Palau',
		PY: 'Paraguay',
		QA: 'Qatar',
		RE: 'Reunion',
		RO: 'Romania',
		RS: 'Serbia',
		RU: 'Russian Federation',
		RW: 'RWANDA',
		SA: 'Saudi Arabia',
		SB: 'Solomon Islands',
		SC: 'Seychelles',
		SD: 'Sudan',
		SE: 'Sweden',
		SG: 'Singapore',
		SH: 'Saint Helena',
		SI: 'Slovenia',
		SJ: 'Svalbard and Jan Mayen',
		SK: 'Slovakia',
		SL: 'Sierra Leone',
		SM: 'San Marino',
		SN: 'Senegal',
		SO: 'Somalia',
		SR: 'Suriname',
		ST: 'Sao Tome and Principe',
		SV: 'El Salvador',
		SY: 'Syrian Arab Republic',
		SZ: 'Eswatini',
		TC: 'Turks and Caicos Islands',
		TD: 'Chad',
		TF: 'French Southern Territories',
		TG: 'Togo',
		TH: 'Thailand',
		TJ: 'Tajikistan',
		TK: 'Tokelau',
		TL: 'Timor-Leste',
		TM: 'Turkmenistan',
		TN: 'Tunisia',
		TO: 'Tonga',
		TR: 'Turkey',
		TT: 'Trinidad and Tobago',
		TV: 'Tuvalu',
		TW: 'Taiwan, Province of China',
		TCYP: "Turkish Cyprus",
		TZ: 'Tanzania, United Republic of',
		UA: 'Ukraine',
		UG: 'Uganda',
		UM: 'United States Minor Outlying Islands',
		US: 'United States',
		UY: 'Uruguay',
		UZ: 'Uzbekistan',
		VA: 'Holy See (Vatican City State)',
		VC: 'Saint Vincent and the Grenadines',
		VE: 'Venezuela',
		VG: 'Virgin Islands, British',
		VI: 'Virgin Islands, U.S.',
		VN: 'Viet Nam',
		VU: 'Vanuatu',
		WF: 'Wallis and Futuna',
		WS: 'Samoa',
		XK: 'Kosovo',
		YE: 'Yemen',
		YT: 'Mayotte',
		YU: "Yugoslavia",
		ZA: 'South Africa',
		ZANZ: "Zanzibar",
		ZM: 'Zambia',
		ZW: 'Zimbabwe'
	}

	// public disableLogout: boolean

	public countriesMenuItems: any[] = [] // https://www.npmjs.com/package/ng-multiselect-dropdown

	public contactCompanyForm = {
		message1: "",
		queryType: "",
		message2: "",
		thirdParty: "",
		thirdPartyName: ""
	}

	public reportInaccuraciesForm = {
		from: "",
		familyId: "",
		message: "",
		companyId: ""
	}

	public urlPrefix = window["urlPrefix"]

	public acceptanceModalHTML: string = ""

	public socketId: string = null; // On socket connection, the back-end generates an id. I emit this id back to the same socket in the front, and store it here. Now, when sending CSV data to the back, I can pass this id along so the back-end will send real-time updates to my own socket only. Other people can always hit the "refresh" button.
	public currentlyProcessedFamilyId: string = "";


	/*
	public filters = { // Persistance of "filter" input fields in table headers --> Changing that to one global state in mas.resultsState
		inns: {
			name: "",
			inn: ""
		},
		families: {
			filterable: ""
		}
	}
	*/

	public familyLanguages: any[] = [] // [{"lang": "en", "source": "WO", content: "..." }]
	public selectedLanguageIndex: number = 0
	public preferredLanguage: string = ""

	public videoInModal = "";

	constructor(public http: HttpClient,
		public router: Router) {

		const l = `ms constructor - `

		Object.defineProperty(this.display, "modal", {
			enumerable: true,
			get: () => this.modal(),
			set: (value: string) => this.modal.set(value)
		});

		/*if(!this.devMode){
			// console.log(`\nMS - Prod mode - Disabling the console. (window.location.hostname = ${window.location.hostname})`)
			window["console"]["log"] = () => {}
		}*/

		this.getCompaniesPublic();

		// this.disableLogout = localStorage.getItem("disableLogout") === "true"

		// https://www.npmjs.com/package/ng-multiselect-dropdown
		this.countriesMenuItems = [
			{ label: 'EP - European Patent Office', cc: 'EP' },
			{ label: 'AP - ARIPO', cc: 'AP' },
			{ label: 'EA - EAPO', cc: 'EA' },
			{ label: 'OA - OAPI', cc: 'OA' },
			{ label: 'GC - Gulf Cooperation Council', cc: 'GC' },
			{ label: 'WO - WIPO', cc: 'WO' },
		];

		Object.keys(this.countries).forEach(cc => {
			if (!this.countriesMenuItems.find(item => item["cc"] == cc)) this.countriesMenuItems.push({
				label: `${cc} - ${this.countries[cc]}`,
				cc: cc
			})
		});

		this.countriesMenuItems.push({ label: 'Other countries/jurisdictions', cc: 'ZZ' })

		// console.log(`\nMechanicsService - urlPrefix = `, this.urlPrefix)

		router.events.subscribe(val => { // Resetting the manager when leaving it (otherwise, all old loaded data is still displayed when another user logs on)
			if (val instanceof NavigationEnd) {
				// console.log(`MS Router event - val.url =`, val.url);
				this.currentUrl = val.url
				// console.log(`MS route subscription - ms.currentUrl='${this.currentUrl}'`)
			}
		})
		/*
				if (this.isLocalHost || this.isDevServer) {
					const head = document.getElementsByTagName('head')[0];
					const js = document.createElement("script");
					js.type = "text/javascript";
					js.src = "https://livejs.com/live.js";
					head.appendChild(js);
				}
		*/






		if (this.isLocalHost) {
			const hostnamesMapping = {
				'www.wipo.int': "PROD",
				'localhost': "LOCALHOST",
				'pat-informed.gbd.globaldb.dev.web1.wipo.int': "AWS DEV",
				'95.111.228.181': "Contabo perso"
			}

			const hostType = hostnamesMapping[window.location.hostname] || `(Unknown host)`;

			document.querySelector("title").innerText = `${hostType} PAT - Dev mode`;
		}

	}

	buildFamilyLanguages(f: Family = this.selectedFamily) {

		const l = `ms.buildFamilyLanguages - `

		// console.log(`${l}f = `, deepClone(f))
		// console.log(`${l}familyLanguages before = `, deepClone(this.familyLanguages))

		this.familyLanguages = f.title.map(obj => ({
			lang: obj["lang"] || "ol",
			source: obj["cc"] || "?"
		}));

		// console.log(`${l}familyLanguages after = `, deepClone(this.familyLanguages))

	}

	// Get the currently selected language code (e.g., "en", "fr", "ar")
	get selectedLanguageCode(): string {
		return this.familyLanguages[this.selectedLanguageIndex]?.lang || "en";
	}

	// Get title object by the selected language code (not by index)
	get selectedTitle(): any {
		if (!this.selectedFamily?.title) return null;
		const lang = this.selectedLanguageCode;
		return this.selectedFamily.title.find(t => t.lang === lang) || this.selectedFamily.title[0];
	}

	// Get abstract object by the selected language code (not by index)
	get selectedAbstract(): any {
		if (!this.selectedFamily?.abstract) return null;
		const lang = this.selectedLanguageCode;
		return this.selectedFamily.abstract.find(a => a.lang === lang) || null;
	}

	computeVipEmails() {
		if (this.vipEmails.length) return

		this.vipEmails = this.companies.map(company => new URL(company["website"])
			.hostname // "www.msd.com"
			.split(".") // ["www","msd","com"]
		[1] // "msd"
		)

		this.companies.forEach(comp => {
			if (comp.extensions) {
				this.vipEmails = this.vipEmails.concat(comp.extensions)
			}
		})

		// console.log(`\nBuilt this.vipEmails = `, this.vipEmails)
	}

	async getCompaniesPublic() {

		const l = `ms.getCompaniesPublic() - `

		let body;

		try {
			body = await lastValueFrom(this.http.post(`${environment.apiUrl}${window['urlPrefix']}/api/getCompaniesPublic`, {}));

			// console.log(`${l}body = `, body)

			this.isLoading = false
			this.companies = body.sort((a, b) => a.displayedName > b.displayedName ? 1 : -1)
			window["companies"] = this.companies
			this.computeVipEmails()
		} catch (err) {
			this.isLoading = false
			console.error(err)
			if (err.status === 401) return this.navigate('/')
		}
	}

	navigate(url) {
		this.router.navigate([url])
		// this.currentUrl = url
		sessionStorage.setItem("patInfUrl", url)
	}

	async getDisclaimerOnce(): Promise<any> {

		// console.log(`Getting disclaimerOnce...`)
		try {
			const res = await lastValueFrom(this.http.get(`/assets/documents/disclaimerOnce.html`, { responseType: 'text' }));
			this.acceptanceModalHTML = res["_body"] || res
			// console.log(`Got disclaimer = `, this.acceptanceModalHTML)
		} catch (err) {
			console.error(err)
		}
	}


	/*
		Modal stuff
	*/

	toggleModal(name: string, caller: string = "?") { // "Login", "PrincipalSections", without "Modal"

		const l = `ms.toggleModal() - `

		name = name
			.replace(/^modal/i, "") // foolproof
			.replace(/^([A-Z])/, match => match?.toLowerCase());

		this.setModal(name);

		// console.log(`${l}toggling modal "${name}"`);

		setTimeout(() => {
			if (!this.modal().length) return;

			const dialogId = window.location.href.includes("/manager/") ? "modalsManager" : "modalsHomepage";
			const dialog = document.getElementById(dialogId) as HTMLDialogElement;
			if (dialog && !dialog.open) dialog.showModal();
		}, 0);
	}

	setModal(name: string) {
		this.modal.set(name);
	}

	get currentModal(): string {

		const openModals: string[] = Object.keys(this.display)
			.filter(key => key.startsWith("modal") && key !== "modalOverlay" && this.display[key])

		return openModals[0] || null;

	}

	closeCurrentModal(caller: string = "unknown") {

		const l = `ms.closeCurrentModal() - `

		this.setModal("");
		const dialogId = window.location.href.includes("/manager/") ? "modalsManager" : "modalsHomepage";
		const dialog = document.getElementById(dialogId) as HTMLDialogElement;
		if (dialog?.open) dialog.close();
	}
}


